import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { of, Subject } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { LaunchDarklyFeatureKey } from 'src/app/shared/launch-darkly-feature-key';
import { LoginState } from 'src/app/shared/login/login-service/login-state.interface';
import { LoginService } from 'src/app/shared/login/login-service/login.service';
import { NotificationService } from 'src/app/shared/notification/notification.service';
import { CardConnectCardOnFile } from './card-connect-card-on-file.interface';
import { StripeCardOnFile } from './stripe-card-on-file.interface';
import { CardOnFile } from './card-on-file';
import { CreditCardProcessingMethod } from 'src/app/shared/credit-card-processing-method.enum';
import { LaunchDarklyService } from 'src/app/services/launch-darkly-service/launch-darkly-service';
import { CreditCardProcessingService } from 'src/app/shared/credit-card-processing/credit-card-processing.service';
import { StripeSurchargeSettings } from '../stripe-surcharge-settings.interface';

const cardConnectQuery = gql`
    query Query($customerID: Int!, $clientID: Int!) {
        cardsOnFile(customerID: $customerID, clientID: $clientID) {
            accountID
            cardType
            token
            expireMonth
            expireYear
        }
    }
`;

const stripeQuery = gql`
    query GetStripeSavedCards($customerID: Int!, $clientID: String!) {
        stripeSavedCards(customerID: $customerID, clientID: $clientID) {
            paymentMethodID
            brand
            country
            expMonth
            expYear
            fingerprint
            last4
        }
    }
`;

@Component({
    selector: 'ols-payment-information',
    templateUrl: './payment-information.component.html',
    styleUrls: ['./payment-information.component.scss'],
})
export class PaymentInformationComponent implements OnInit, OnDestroy {
    @Input() isLoading: boolean;
    @Input() purchaseAgreement: string;
    @Input() stripeSurchargeSettings: StripeSurchargeSettings | null = null;
    @Input() summary: any;

    private ngUnsubscribe: Subject<any> = new Subject();
    public cardsOnFile: CardOnFile[] = [];

    constructor(
        private launchDarklyService: LaunchDarklyService,
        private apollo: Apollo,
        private loginService: LoginService,
        private notificationService: NotificationService,
        private creditCardProcessingService: CreditCardProcessingService
    ) {}

    ngOnInit() {
        this.loginService.loginState$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((updatedState: LoginState) => {
                if (updatedState?.clientID) {
                    this.loadCardsForClient(updatedState.clientID);
                } else {
                    this.cardsOnFile = [];
                }
            });
    }

    private loadCardsForClient(clientID: number) {
        const creditCardProcessingMethod = this.creditCardProcessingService.getCreditCardProcessingMethod();
        if (creditCardProcessingMethod === null) {
            return;
        } else if (
            creditCardProcessingMethod ===
            CreditCardProcessingMethod.CardConnect
        ) {
            this.cardsOnFile = null; // Trigger loading state
            this.loadCardConnectCards(clientID);
        } else if (
            creditCardProcessingMethod === CreditCardProcessingMethod.Stripe
        ) {
            this.cardsOnFile = null; // Trigger loading state
            this.loadStripeCards(clientID);
        }
    }

    private loadCardConnectCards(clientID: number) {
        const variables = {
            customerID: parseInt(
                localStorage.getItem('Customer_ID') || '0',
                10
            ),
            clientID: clientID,
        };
        this.apollo
            .query({
                query: cardConnectQuery,
                variables: variables,
                fetchPolicy: 'network-only',
            })
            .pipe(
                takeUntil(this.ngUnsubscribe),
                tap((queryResponse: any) => {
                    if (queryResponse?.data?.cardsOnFile?.length > 0) {
                        this.cardsOnFile = queryResponse.data.cardsOnFile.map(
                            (card) => {
                                const cardConnectCard: CardConnectCardOnFile = {
                                    accountID: card.accountID,
                                    cardType: this.getCardTypeName(
                                        card.cardType
                                    ),
                                    token: card.token.slice(-4),
                                    expirationStatus: this.getExpirationStatus(
                                        card.expireMonth,
                                        card.expireYear
                                    ),
                                    isExpired: this.isCardExpired(
                                        card.expireMonth,
                                        card.expireYear
                                    ),
                                };
                                return new CardOnFile(cardConnectCard);
                            }
                        );
                    } else if (queryResponse?.errors) {
                        this.notificationService.showFailure(
                            queryResponse.errors
                        );
                        this.cardsOnFile = [];
                    } else {
                        this.cardsOnFile = [];
                    }
                }),
                catchError((err) => {
                    this.notificationService.showFailure(err);
                    this.cardsOnFile = [];
                    return of(null);
                })
            )
            .subscribe();
    }

    private loadStripeCards(clientID: number) {
        const variables = {
            customerID: parseInt(
                localStorage.getItem('Customer_ID') || '0',
                10
            ),
            clientID: clientID.toString(),
        };
        this.apollo
            .query({
                query: stripeQuery,
                variables: variables,
                fetchPolicy: 'network-only', // Force fresh data
            })
            .pipe(
                takeUntil(this.ngUnsubscribe),
                tap((queryResponse: any) => {
                    if (queryResponse?.data?.stripeSavedCards?.length > 0) {
                        this.cardsOnFile = queryResponse.data.stripeSavedCards.map(
                            (card) => {
                                const stripeCard: StripeCardOnFile = {
                                    paymentMethodID: card.paymentMethodID,
                                    brand: this.getCardTypeName(card.brand),
                                    country: card.country,
                                    last4: card.last4,
                                    expMonth: card.expMonth,
                                    expYear: card.expYear,
                                    fingerprint: card.fingerprint,
                                    expirationStatus: this.getExpirationStatus(
                                        card.expMonth,
                                        card.expYear
                                    ),
                                    isExpired: this.isCardExpired(
                                        card.expMonth,
                                        card.expYear
                                    ),
                                };
                                return new CardOnFile(undefined, stripeCard);
                            }
                        );
                    } else if (queryResponse?.errors) {
                        this.notificationService.showFailure(
                            queryResponse.errors
                        );
                        this.cardsOnFile = [];
                    } else {
                        this.cardsOnFile = [];
                    }
                }),
                catchError((err) => {
                    this.notificationService.showFailure(err);
                    this.cardsOnFile = [];
                    return of(null);
                })
            )
            .subscribe();
    }

    public isLoginOnCheckoutEnabled(): boolean {
        const showUserAccountEnabled =
            localStorage.getItem('User_Account_Enabled') === 'true';
        return (
            this.launchDarklyService.isFeatureActive(
                LaunchDarklyFeatureKey.LoginOnCheckout
            ) && showUserAccountEnabled
        );
    }

    private getCardTypeName(type: string): string {
        switch (type) {
            case 'AMEX':
                return 'American Express';
            case 'DISC':
                return 'Discover';
            case 'MC':
                return 'MasterCard';
            case 'VISA':
                return 'Visa';
            default:
                return 'Credit';
        }
    }

    private getExpirationStatus(
        expireMonth: number,
        expireYear: number
    ): string {
        const status: string = this.isCardExpired(expireMonth, expireYear)
            ? `Expired ${expireMonth}/${expireYear}`
            : `Expires ${expireMonth}/${expireYear}`;

        return status;
    }

    private isCardExpired(expireMonth: number, expireYear: number): boolean {
        const currentDate = new Date();
        const currentYear: number = currentDate.getFullYear();
        const currentMonth: number = currentDate.getMonth() + 1;

        const isExpired: boolean =
            currentYear > expireYear ||
            (currentMonth > expireMonth && currentYear === expireYear);

        return isExpired;
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
